import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

interface PolicyContentProps {
  /** Raw markdown content to render */
  content: string;
  /** Optional className for styling */
  className?: string;
}

// Only override link rendering for security — all visual styling handled by .policy-content in globals.css
const markdownComponents: Components = {
  a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
};

/**
 * Component for rendering policy markdown content with XSS protection.
 *
 * Features:
 * - Renders markdown with GitHub-flavored markdown support (tables, strikethrough, etc.)
 * - Sanitizes HTML to prevent XSS attacks
 * - Opens links in new tabs with security attributes
 * - Typography and layout styled via .policy-content in globals.css
 * - Proper heading hierarchy for accessibility
 *
 * @example
 * ```tsx
 * const { data } = usePolicy('terms');
 * return <PolicyContent content={data.content} />;
 * ```
 */
export function PolicyContent({ content, className = '' }: PolicyContentProps) {
  // Handle empty content
  if (!content || content.trim() === '') {
    return (
      <div className={className} role="article" aria-label="Policy content">
        <p className="text-muted-foreground italic">No content available.</p>
      </div>
    );
  }

  return (
    <div
      className={`policy-content max-w-none ${className}`}
      role="article"
      aria-label="Policy content"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
